import store from '@/store'
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import Vue from "vue";
import { Decision } from "@/api/model/Decision";
import { VIEWPOINT_API } from "@/api/ViewpointApi";
import _ from "lodash";
import { Viewpoint } from "@/api/model/Viewpoint";
import Viewpoints from "@/store/modules/Viewpoints";
import Modelling from "@/store/modules/Modelling";
import Comments from "@/store/modules/Comments";
import Choices, { Choice } from "@/store/modules/Choices";
import Apps from "@/store/modules/Apps";
import Users from '@/store/modules/Users'
import { v4 as uuidv4 } from 'uuid';
import Scoring from "@/store/modules/Scoring";
import WorkspaceOptions from '@/store/modules/WorkspaceOptions';
import { API, Storage } from 'aws-amplify';
import { DecisionUser } from "@/api/model/DecisionUser";
// import WorkspaceOptions from './WorkspaceOptions';
import { ResourceList } from '@/api/model/ResourceList';
import { FactorResource } from '@/api/model/FactorResource';
import { ViewpointChoiceFactor } from '@/api/model/ViewpointChoiceFactor';

config.rawError = true

const name = 'decisions';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}

@Module({ dynamic: true, store: store, name: name, namespaced: true })
export default class Decisions extends VuexModule {

    decisions: { [decisionId: string]: Decision } = {};

    decisionViewpoints: { [decisionId: string]: Viewpoint[] } = {};

    selectedDecisionUsers: { [userId: string]: DecisionUser } = {};

    selectedDecisionId: string | null = null;

    selectedDecisionShareInformation: any = {};

    decisionLists: { [listId: string]: ResourceList } = {};

    cloneDecisionProgress = "";

    allDecisionUsers: any = {};

    // get if the user is the decision admin
    get isAdmin() {
        if (this.selectedDecisionUsers
            && getModule(Users).currentUserProfile
        ) {
            let userId = getModule(Users).currentUserProfile.userId;
            if (this.selectedDecisionUsers[userId]){
                return this.selectedDecisionUsers[userId].permissionAccess === 'ADMIN'
            }else {
                return false;
            }
        } else {
            return false;
        }
    }
    
    get isAdminOrOwner(): boolean {
        return this.isAdmin || this.isOwner;
    }

    // get if the current user is the decision owner
    get isOwner() {
        if (this.selectedDecisionUsers
            && getModule(Users).currentUserProfile
        ) {
            let userId = getModule(Users).currentUserProfile.userId;
            if (this.selectedDecisionUsers[userId]) {
                return this.selectedDecisionUsers[userId].permissionAccess === 'OWNER';
            } else {
                return false;
            }
        } else {
            return false;
        }
    }


    get selectedDecision(): Decision | null {
        if (this.selectedDecisionId && this.decisions) {
            return this.decisions[this.selectedDecisionId];
        }
        return null;
    }

    get singularChoiceLabel() {
        if (this.selectedDecision && this.decisions) {
            return (this.selectedDecision.choiceLabel) ? this.selectedDecision.choiceLabel : 'Choice'
        }
        return "Choice"
    }

    get pluralChoiceLabel() {
        if (this.selectedDecision && this.decisions && this.singularChoiceLabel) {
            return (this.selectedDecision.pluralChoiceLabel) ? this.selectedDecision.pluralChoiceLabel : `${this.singularChoiceLabel}(s)`
        }
        return "Choices"
    }

    get singularViewpointLabel() {
        if (this.selectedDecision && this.decisions) {
            return (this.selectedDecision.viewpointLabel) ? this.selectedDecision.viewpointLabel : 'Viewpoint'
        }
        return "Viewpoint"
    }

    get pluralViewpointLabel() {
        if (this.selectedDecision && this.decisions && this.singularViewpointLabel) {
            return (this.selectedDecision.pluralViewpointLabel) ? this.selectedDecision.pluralViewpointLabel : `${this.singularViewpointLabel}(s)`
        }
        return "Viewpoints"
    }

    get singularFactorLabel() {
        if (this.selectedDecision && this.decisions) {
            return (this.selectedDecision.factorLabel) ? this.selectedDecision.factorLabel : 'Factor'
        }
        return "Factor"
    }

    get pluralFactorLabel() {
        if (this.selectedDecision && this.decisions && this.singularFactorLabel) {
            return (this.selectedDecision.pluralFactorLabel) ? this.selectedDecision.pluralFactorLabel : `${this.singularFactorLabel}(s)`
        }
        return "Factors"
    }

    get scoringSuffix() {
        if (this.selectedDecision && this.decisions) {
            return this.selectedDecision.scoringSuffix ? this.selectedDecision.scoringSuffix : '%'
        }
    }

    get scoringRangeMax() {
        if (this.selectedDecision && this.decisions) {
            return this.selectedDecision.scoringRangeMax ? this.selectedDecision.scoringRangeMax : 100
        }
    }

    get relatedDecisions() {
        let tempObject = {}
        let templateDecisions = Object.values(this.decisions).filter((decision: Decision) => decision.decisionTemplateId && decision.decisionTemplateId != '')
        for(let decision of templateDecisions) {
            if(!(decision.decisionTemplateId in tempObject)) {
                tempObject[decision.decisionTemplateId] = [ {name: decision.name, decisionId: decision.decisionId}]
            }
            else {
                tempObject[decision.decisionTemplateId].push({name: decision.name, decisionId: decision.decisionId})
            }
        }
        return tempObject;
    }

    @Mutation
    clear() {
        this.decisions = {};
        this.selectedDecisionId = null;
        this.selectedDecisionShareInformation = {};
        this.decisionLists = {};
    }

    @Mutation
    setSelectedDecisionId(decisionId: string | null) {
        this.selectedDecisionId = decisionId;
    }

    @Mutation
    setDecisions(decisions: { [decisionId: string]: Decision }) {
        this.decisions = decisions;
    }

    @Mutation
    setDecision(decision: Decision) {
        Vue.set(this.decisions, decision.decisionId, decision);
    }

    @Mutation
    addDecision(decision: Decision) {
        Vue.set(this.decisions, decision.decisionId, decision);
    }

    @Mutation
    removeDecision(decisionId: string) {
        Vue.delete(this.decisions, decisionId);
    }

    @Mutation
    setSelectedDecisionUsers(users: { [userId: string]: DecisionUser }) {
        this.selectedDecisionUsers = users;
    }

    @Mutation
    setAllDecisionUsers(allDecisionUsers: any) {
        this.allDecisionUsers = allDecisionUsers;
    }

    @Mutation
    async setSelectedDecisionUserPermission( payload: { id: string, permission: string } ) {   
        if (this.selectedDecisionUsers[payload.id]) {
            Vue.set(this.selectedDecisionUsers[payload.id], 'permissionAccess', payload.permission)
        }
    
    }

    @Mutation
    setDecisionViewpoints(payload: { decisionId: string, viewpoints: Viewpoint[] }) {
        Vue.set(this.decisionViewpoints, payload.decisionId, payload.viewpoints);
    }

    @Mutation
    setDecisionViewpoint(payload: { decisionId: string, viewpoint: Viewpoint }) {
        let index = this.decisionViewpoints[payload.decisionId].findIndex(v => v.viewpointId === payload.viewpoint.viewpointId);
        if (index >= 0) {
            this.decisionViewpoints[payload.decisionId].splice(index, 1, payload.viewpoint)
        }
    }

    @Mutation
    setDecisionScoringKeyDefinition(payload: { decisionId: string, scoringKeyDefinition: any }) {
        Vue.set(this.decisions[payload.decisionId], 'scoringKeyDefinition', payload.scoringKeyDefinition);
    }

    @Mutation
    addDecisionViewpoint(payload: { decisionId: string, viewpoint: Viewpoint }) {
        if (!this.decisionViewpoints[payload.decisionId]) {
            Vue.set(this.decisionViewpoints, payload.decisionId, []);
        }
        if (!this.decisionViewpoints[payload.decisionId].includes(payload.viewpoint)) {
            this.decisionViewpoints[payload.decisionId].splice(this.decisionViewpoints[payload.decisionId].length, 0, payload.viewpoint)
        }
    }

    @Mutation
    setCloneDecisionProgress(progress: string) {
        this.cloneDecisionProgress = progress;
    }

    @Mutation
    /**
     * Remove the viewpoint from Vuex
     * @param  {decisionId:string; viewpointId: string}
     */
    removeDecisionViewpoint(payload: { decisionId: string, viewpointId: string }) {
        let viewpointIndex = this.decisionViewpoints[payload.decisionId]
            .findIndex(f => f.viewpointId === payload.viewpointId);
        if (viewpointIndex >= 0) {
            this.decisionViewpoints[payload.decisionId].splice(viewpointIndex, 1);
        }
    }

    @Mutation
    removeDecisionUser(userId: string) {
        Vue.delete(this.selectedDecisionUsers, userId);
    }

    @Mutation
    addSelectedDecisionUser(inviteUser: any) {
        Vue.set(this.selectedDecisionUsers, inviteUser.userId, inviteUser);
    }

    @Mutation
    setSelectedDecisionUser(decisionUser: any) {
        Vue.set(this.selectedDecisionUsers, decisionUser.userId, decisionUser);
    }

    @Mutation
    setDecisionsLists(lists: { [listId: string]: ResourceList }) {
        this.decisionLists = lists;
    }

    @Action
    async fetchDecisions() {
        let decisions = await VIEWPOINT_API.getUserDecisions()
        let decisionMap = _.keyBy(decisions, 'decisionId');
        // This can be used async
        // let allDecisionUsers = this.getAllDecisionUsers(decisionMap);

        // Can be switch to await to ensure data is ready to use
        // let allDecisionUsers = await this.getAllDecisionUsers(decisionMap);
        // this.setAllDecisionUsers(allDecisionUsers);

        this.setDecisions(decisionMap);
        return decisionMap;
    }

    // Used in Drupal PDB Blocks but was tested here and left just in case we used it
    @Action
    async getAllDecisionUsers(decisionMap: any) {
        let allDecisionUsers: any = {};
        let promises: any[] = [];
        for (const decisionId in decisionMap) {
            promises.push(
                VIEWPOINT_API.getDecisionUsers(decisionId).then( (response: any) => {
                    // console.log('VIEWPOINT_API.getDecisionUsers(decisionId)');
                    // console.log(response);
                    let userMap = _.keyBy(response, 'userId');

                    // Send the all the User objects
                    // allDecisionUsers[decisionId] = userMap;
                    // Or just send the Ids
                    allDecisionUsers[decisionId] = Object.keys(userMap);
                })
            );
        }
        return Promise.all(promises).then( () => {
            // console.log('getAllDecisionUsers(decisionMap: any)');
            // console.log(allDecisionUsers);
            return allDecisionUsers;
        } );
    }

    @Action
    async selectDecision(decisionId: string) {
        if (this.selectedDecisionId === decisionId) return;
        this.setSelectedDecisionId(decisionId);
        let modellingModule = getModule(Modelling);
        getModule(WorkspaceOptions).clear();
        modellingModule.clear();
        getModule(Choices).clear();
        getModule(Viewpoints).clear();

        // getModule(WorkspaceOptions).clear();
        if (decisionId && this.decisions.hasOwnProperty(decisionId)) {
            let decisionsListsPromise = VIEWPOINT_API.getDecisionLists(decisionId);
            let decisionUsersPromise = VIEWPOINT_API.getDecisionUsers(decisionId);
            let publishedModelPromise = modellingModule.getPublishedDecisionModel(decisionId);
            let workingModelPromise = modellingModule.getWorkingDecisionModel(decisionId);
            // if (!this.decisionViewpoints.hasOwnProperty(decisionId)) {
            let decisionViewpoints = await this.getDecisionViewpoints(decisionId);
            
            // }
            this.setDecisionsLists(_.keyBy(await decisionsListsPromise, 'listId'));
            this.setSelectedDecisionUsers(_.keyBy(await decisionUsersPromise, 'userId'));
            await Promise.all([publishedModelPromise, workingModelPromise]);
            // moved for annoymounce users
            await getModule(Choices).fetchChoices();

            await getModule(Apps).fetchAppsByDecision(decisionId).then((res: any)=>{}).catch((error:any) => {
                console.error(error);
            });
            await getModule(Apps).fetchMarketplaceItems().then((res: any) => { }).catch((error: any) => {
                console.error(error);
            });
            // getModule(Scoring).fetchViewpointScoring();
            // getModule(Users).getSelectedDecisionUsers();
            getModule(Comments).fetchComments();
            await modellingModule.checkDiff()
        }
    }

    @Action
    async createDecision(payload: { name: string, description?: string, choiceLabel?: string, viewpointLabel?: string, factorLabel?: string, pluralChoiceLabel?: string, pluralViewpointLabel?: string, pluralFactorLabel?: string, scoringSuffix?: string, scoringRangeMax?: number, commentAdminTags?: string[], commentUserTags?: string[], tagsDecision?: string[], tagsDataScience?: string[], tagsMarketplace?: string[], templateApprovedKeys?: string[], stripeProductCode?: string, templateIcon?: string, templateLogo?: string, templateLogoKey?: string, templateImageCard?: string, templateImageCardKey?: string, templateImagePage?: string, templateImagePageKey?: string, templateDescriptionCard?: string, templateDescriptionPage?: string, templateTagline?: string, decisionTemplateId?: string, templatePriceSingle?: number, templatePriceMonthly?: number, templateApproved?: boolean, templateEnabled?: boolean, fromTemplateId?: string }) {
        let publisherName = '';
        let publisherFirstName = getModule(Users).currentUserProfile?.first;
        let publisherLastName = getModule(Users).currentUserProfile?.last;
        if (publisherFirstName && publisherLastName) {
            publisherName = publisherFirstName + ' ' + publisherLastName;
        } else if (publisherFirstName && !publisherLastName) {
            publisherName = publisherFirstName;
        } else if (!publisherFirstName && publisherLastName) {
            publisherName = publisherLastName;
        }
        let newDecision = await VIEWPOINT_API.createDecision(payload.name, (payload.description || undefined), (payload.choiceLabel || undefined), (payload.viewpointLabel || undefined), (payload.factorLabel || undefined), (payload.pluralChoiceLabel || undefined), (payload.pluralViewpointLabel || undefined), (payload.pluralFactorLabel || undefined), (payload.scoringSuffix || '%'), (payload.scoringRangeMax || 100), (payload.commentAdminTags || []), (payload.commentUserTags || []), (payload.tagsDecision || []), (payload.tagsDataScience || []), (payload.tagsMarketplace || []), (payload.templateApprovedKeys || []), (payload.stripeProductCode || undefined), (payload.templateIcon || undefined), (payload.templateLogo || undefined), (payload.templateImageCard || undefined), (payload.templateImagePage || undefined), (payload.templateDescriptionCard || undefined), (payload.templateDescriptionPage || undefined), (payload.templateTagline || undefined), (payload.decisionTemplateId || undefined), (payload.templatePriceSingle || undefined), (payload.templatePriceMonthly || undefined), (payload.templateApproved || false), (payload.templateEnabled || false), publisherName, (payload.fromTemplateId || undefined));
        this.addDecision(newDecision)
        return newDecision;
    }


    @Action
    async updateDecision(payload: Decision) {
        let publisherName = '';
        let publisherFirstName = getModule(Users).currentUserProfile?.first;
        let publisherLastName = getModule(Users).currentUserProfile?.last;
        if (publisherFirstName && publisherLastName) {
            publisherName = publisherFirstName + ' ' + publisherLastName;
        } else if (publisherFirstName && !publisherLastName) {
            publisherName = publisherFirstName;
        } else if (!publisherFirstName && publisherLastName) {
            publisherName = publisherLastName;
        }
        let updatedDecision = await VIEWPOINT_API.updateDecision(payload.decisionId, payload.name, (payload.description || undefined), (payload.choiceLabel || undefined), (payload.viewpointLabel || undefined), (payload.factorLabel || undefined), (payload.pluralChoiceLabel || undefined), (payload.pluralViewpointLabel || undefined), (payload.pluralFactorLabel || undefined), (payload.group || undefined), (payload.scoringSuffix || '%'), (payload.scoringRangeMax || 100), (payload.commentAdminTags || []), (payload.commentUserTags || []), (payload.tagsDecision || []), (payload.tagsDataScience || []), (payload.tagsMarketplace || []), (payload.templateApprovedKeys || []), (payload.stripeProductCode || undefined), (payload.templateIcon || undefined), payload.templateLogo , payload.templateImageCard,payload.templateImagePage, (payload.templateDescriptionCard || undefined), (payload.templateDescriptionPage || undefined), (payload.templateTagline || undefined), (payload.decisionTemplateId || undefined), (payload.templatePriceSingle || undefined), (payload.templatePriceMonthly || undefined), (payload.templateApproved || false), (payload.templateEnabled || false), publisherName);
        this.setDecision(updatedDecision);
        return updatedDecision;
    }

    @Action
    async deleteDecision(decisionId: string) {
        let deletedDecision = await VIEWPOINT_API.deleteDecision(decisionId);
        if (this.selectedDecisionId === decisionId) {
            await this.selectDecision('')
        }
        this.removeDecision(decisionId)
        // remove audio files in the S3 storage
        try {
            const audioList = await Storage.list(`audio/${decisionId}`);
            if(audioList.length> 0 ){
                audioList.forEach(audio => {
                    Storage.remove(audio.key);
                });
            }
        } catch (e) {
            console.error(e);
        }
        return deletedDecision;
    }

    @Action
    async createDecisionViewpoint(payload: { decisionId: string, name: string, description: string, skipSetActive?: boolean, createdFromId?: string }) {

        let newViewpoint = await VIEWPOINT_API.createViewpoint(payload.decisionId, payload.name, payload.description, payload.createdFromId);
        await this.addDecisionViewpoint({ decisionId: payload.decisionId, viewpoint: newViewpoint });
        if(newViewpoint?.viewpointId && payload.skipSetActive != true) {
            await getModule(Viewpoints).setActiveViewpointId(newViewpoint.viewpointId);
            await getModule(Viewpoints).addSelectedViewpointId(newViewpoint.viewpointId);
            await getModule(Viewpoints).selectViewpoint(newViewpoint.viewpointId);
        }
        return newViewpoint;
    }


    @Action
    async deleteViewpoint(viewpointId: string) {
        // remove viewpoint from db
        let deletedViewpoint = await VIEWPOINT_API.deleteViewpoint(viewpointId);
        // remove the viewpoint from Vuex
        this.removeDecisionViewpoint({
            decisionId: deletedViewpoint.decisionId,
            viewpointId: deletedViewpoint.viewpointId
        })
        let viewpointsModule = getModule(Viewpoints);
        viewpointsModule.removeSelectedViewpointId(viewpointId);
        // delete viewpoints from list manager
        this.deleteViewpointFromList();
        if (viewpointId === viewpointsModule.activeViewpointId) {
            if (viewpointsModule.selectedViewpointIds && viewpointsModule.selectedViewpointIds.length > 0) {
                viewpointsModule.selectActiveViewpoint(viewpointsModule.selectedViewpointIds[0]);
            } else {
                viewpointsModule.selectActiveViewpoint();
            }
        }
        return deletedViewpoint;
    }

    @Action
    async deleteMultipleViewpoints(viewpoints: Viewpoint[]) {
        for (let viewpoint of viewpoints) {
            await this.deleteViewpoint(viewpoint.viewpointId);
        }
    }

    @Action
    async copyIntoNewDecision(payload: {decision: Decision, params: any}) {
        let params = payload.params;
        // kathy - getModule(Modelling).publishedModelFactorResources['M'] - 
        // this is the current decisision factors not the template decision factors
        //  i don't want to modify this function, so I created a new function to copy the factors from template
        //let map = await getModule(Modelling).pasteNewDecision({destinationDecision: result.decisionId, destinationId: 'M', factorResource: getModule(Modelling).publishedModelFactorResources['M']} )
        let copyResult = await this.copyTemplateIntoNewDecision({decision: payload.decision, newTemplateDecisionId: params.newTemplateDecisionId, newDecisionName: params.newDecisionName, options: params.options});
        return copyResult;
    }


    // kathy - copy template into a new decision
    // param:
    // decision: template decision copy from
    // newDecisionName: New name for the decision to help organize the decisions better
    @Action
    async copyTemplateIntoNewDecision(payload: {decision: any, newTemplateDecisionId: string, newDecisionName: string, options:{
        includeChoices: boolean, includeViewpoints: boolean, includeApps: boolean, includeLists: boolean, includeComments?: boolean, includeUsers: boolean,
    }}) {
        this.setCloneDecisionProgress('Initializing')
        let viewpointMap = {};
        let choiceMap = {};
        // clone the old decision for copying
        let newDecision = _.cloneDeep(payload.decision);
        // replace the newDecision with  the new decision name
        newDecision.name = payload.newDecisionName;
        newDecision.templateLogo = undefined;
        newDecision.templateImageCard = undefined;
        newDecision.templateImagePage = undefined;
        newDecision.decisionTemplateId = payload.newTemplateDecisionId;
        newDecision.fromTemplateId = payload.decision.decisionId;
        newDecision.templateApproved = false;
        newDecision.templateEnabled = false;

        // create new Decision
        let originalDecisionId = 'decisionIdRef' in payload.decision ? payload.decision.decisionIdRef: payload.decision.decisionId
        this.setCloneDecisionProgress('Creating New Decision')
        let result: Decision = await this.createDecision(newDecision);
        // if(result.templateLogoKey){
        //     let logoFile = await Storage.get(result.templateLogoKey, {download:true});
        //     await Storage.put(`decisions/${result.decisionId}/${logoFile.body}`,logoFile);
        // } 
        let map = [];
        let mapDict = {};
        map.push({oldId:payload.decision.decisionId, newId:result.decisionId});
        mapDict[payload.decision.decisionId] = result.decisionId;
        // console.log(payload.decision.choicesSourceId);
        // get choices from the old decision template
        try {
            let choices = await VIEWPOINT_API.getChoices(payload.decision.choicesSourceId);
            // create choices into the new decision
            for (let choice of choices.choices) {
                if (payload.options.includeChoices) {
                    this.setCloneDecisionProgress(`Copying over Choices`)
                    let copyChoice = _.cloneDeep(choice);
                    copyChoice.id = '';
                    copyChoice.choicesSourceId = result.choicesSourceId;
                    copyChoice.ownerUserId = '';
                    let newChoice = await VIEWPOINT_API.createChoice(result.choicesSourceId, copyChoice);
                    map.push({ oldId: choice.id, newId: newChoice.id });
                    mapDict[choice.id] = newChoice.id;
                } else {
                    map.push({ oldId: choice.id, newId: '' });
                    mapDict[choice.id] = '';
                }
            }
        } catch (error) {
            console.error(error);
        }
            
        try {
            let viewpoints = await this.getDecisionViewpoints(originalDecisionId);
            // create new viewpoints in the new decision
            for (let viewpoint of viewpoints) {
                if (payload.options.includeViewpoints) {
                    this.setCloneDecisionProgress(`Copying over Viewpoints`)
                    let cloneViewpoint = _.cloneDeep(viewpoint);
                    cloneViewpoint.decisionId = result.decisionId;
                    cloneViewpoint.viewpointId = "";
                    cloneViewpoint.ownerUserId = "";
                    let newViewpoint = await this.createDecisionViewpoint({ decisionId: result.decisionId, name: cloneViewpoint.name, description: cloneViewpoint.description, skipSetActive: true, createdFromId: viewpoint.viewpointId });
                    map.push({ oldId: viewpoint.viewpointId, newId: newViewpoint.viewpointId });
                    mapDict[viewpoint.viewpointId] = newViewpoint.viewpointId;
                    if (payload.options.includeChoices) {
                        await Decisions.copyReplaceViewpointChoiceScores(newViewpoint.viewpointId, viewpoint.viewpointId, mapDict)
                    }
                }else{
                    map.push({ oldId: viewpoint.viewpointId, newId: '' });
                    mapDict[viewpoint.viewpointId] = '';
                }
            }
        } catch (error) {
            console.error(error);
        }
        if (payload.options.includeLists) {
            //  get decision list from backend
            this.setCloneDecisionProgress('Importing lists')
            let decisionList = await VIEWPOINT_API.getDecisionLists(originalDecisionId);
            // clone to newList for id clean up
            for (let alist of decisionList) {
                let newList = _.cloneDeep(alist)
                let resultList:any;
                switch (newList.type) {
                    case 'FACTOR':
                        resultList = await VIEWPOINT_API.createLists(result.decisionId, [newList]);
                        break;
                    case 'CHOICE':
                        if (payload.options.includeChoices) {
                            this.replaceIdsInList({ list: newList, mapDict: mapDict, newDecisionId: result.decisionId });
                            resultList = await VIEWPOINT_API.createLists(result.decisionId, [newList]);
                        }
                        break;
                    case 'VIEWPOINT':
                        if (payload.options.includeViewpoints) {
                            this.replaceIdsInList({ list: newList, mapDict: mapDict, newDecisionId: result.decisionId });
                            resultList = await VIEWPOINT_API.createLists(result.decisionId, [newList]);
                        }
                        break;
                    default:
                        continue;

                }

                if(resultList != null) {
                    map.push({ oldId: alist.listId, newId: resultList[0].listId });
                }
            }
        }
            
        // console.log(map);
        // create new apps, replace all the decisionId, choiceId and viewpointId in URL
        if (payload.options.includeApps) {
            this.setCloneDecisionProgress('Importing Apps');
            let apps = await VIEWPOINT_API.getApps(originalDecisionId);
            let newApps = _.cloneDeep(apps);
            this.replaceIdsInApps({ apps: newApps, map: map });
            for (let app of newApps) {
                await VIEWPOINT_API.createApp(result.decisionId, app);
            }
        }

        if(payload.options.includeComments) {
            this.setCloneDecisionProgress(`Copying over Comments`);
            await getModule(Comments).cloneComments({newDecisionId: result.decisionId, oldDecisionId: originalDecisionId, map: mapDict, includeChoices: payload.options.includeChoices, includeViewpoints: payload.options.includeViewpoints});
        }

        //copy users to the Decision
        if (payload.options.includeUsers) {
            this.setCloneDecisionProgress(`Copying Users to a new Decision`);
            let users = await VIEWPOINT_API.getDecisionUsers(payload.decision.decisionId)
            users.forEach(user => {
                if (user.permissionAccess.toUpperCase() !== 'OWNER' ) {
                    this.cloneUsersToDecision({'decisionId': result.decisionId, 'userId': user.userId, 'permission': user.permissionAccess});
                }
            });  
        }

        const getUrl = window.location;
        this.setCloneDecisionProgress("Finishing Up")
        const url = getUrl.origin + '/template/builder?decisionId=' + result.decisionId;
        window.location.href = url;
        return {decision: newDecision, map: mapDict};
    }

    @Action
    private replaceIdsInList(payload: {list: any, mapDict: {}, newDecisionId:string}) {
            let alist = payload.list
            alist.listId = null;
            alist.decisionId = payload.newDecisionId;
            alist.ownerUserId = ''
            if (alist.type === 'CHOICE') {
                if( alist.choices && alist.choices.length>0){
                    for (let choice of alist.choices ){
                        choice.choiceId = payload.mapDict[choice.choiceId];
                    }
                }
            }
            if (alist.type === 'VIEWPOINT') {
                if (alist.viewpoints && alist.viewpoints.length > 0) {
                    for (let viewpoint of alist.viewpoints) {
                        viewpoint.viewpointId = payload.mapDict[viewpoint.viewpointId];
                    }
                }
            }
    }
    // function to replace all the ids based on the mapping
    @Action
    private replaceIdsInApps(payload:{apps:any, map:any}){
        let apps = payload.apps;
        let map = payload.map;
        if (apps && apps.length > 0) {
            // this.setIndex(0);
            apps.forEach(app => {
                app.id =''
                if (app.hasOwnProperty('children') && app.children.length > 0) {
                    this.replaceIdInChildren({children: app.children, map: map});
                }
            });
        }
    }

    // replace children's ids based on the map
    @Action
    replaceIdInChildren(payload:{children: any[], map: any}) {
        let idAdded = false
        for ( let child of payload.children)
        {
            child.id = uuidv4();
            for(let item of payload.map){
                if(child.href.indexOf( item.oldId ) >=0 ) {
                    child.href = child.href.replaceAll( item.oldId, item.newId );
                }
            }
            // child.id = this.index;
            // this.indexIncrement();
            if (child.hasOwnProperty('children') && child.children.length > 0) {
                this.replaceIdInChildren({children:child.children, map: payload.map});
            }
        }
    }
    @Action
    async updateViewpoint(payload: Viewpoint) {
        let updatedViewpoint = await VIEWPOINT_API.updateViewpoint(payload);
        this.setDecisionViewpoint({
            decisionId: updatedViewpoint.decisionId,
            viewpoint: updatedViewpoint
        })
        return updatedViewpoint;
    }

    @Action
    async getDecisionViewpoints(decisionId: string) {
        // kat(may04) - if guard prevents the function to get new viewpoints if the application has viewpoints already
        // if (!this.decisionViewpoints.hasOwnProperty(decisionId)) {
        let decisionViewpoints = await VIEWPOINT_API.getDecisionViewpoints(decisionId);
        this.setDecisionViewpoints({ decisionId: decisionId, viewpoints: decisionViewpoints });
        getModule(WorkspaceOptions).resetViewpointColumnsWidth();
        return decisionViewpoints;
        // }
        // return this.decisionViewpoints[decisionId];
    }

    @Action
    async removeUserFromDecision(userId: string) {
        if (!this.selectedDecisionId) return;
        let removed = await VIEWPOINT_API.removeDecisionUser(this.selectedDecisionId, userId);
        this.removeDecisionUser(userId);
        return removed;
    }

    @Action
    async removeUserFromDecisionByEmail(email: string) {
        if (!this.selectedDecisionId) return;
        let user = Object.values(this.selectedDecisionUsers).find(u => u.email === email);
        if (user) {
            return this.removeUserFromDecision(user.userId);
        }
    }

    @Action
    async inviteUserToDecisionByEmail(payload: { email: string, permissions: string }) {
        if (!this.selectedDecisionId) return;
        let inviteUser = await VIEWPOINT_API.inviteDecisionUserByEmail(this.selectedDecisionId, payload.email, payload.permissions)
        if (!inviteUser.userId) inviteUser.userId = inviteUser.email;
        inviteUser.status = 'PENDING'
        this.addSelectedDecisionUser(inviteUser);
    }

    @Action
    async inviteUserToDecisionByUserId(payload: { userId: string, permissions: string }) {
        if (!this.selectedDecisionId) return;
        let inviteUser = await VIEWPOINT_API.inviteDecisionUserByUserId(this.selectedDecisionId, payload.userId, payload.permissions)
        if (!inviteUser.userId) inviteUser.userId = inviteUser.email;
        inviteUser.status = 'PENDING'
        this.addSelectedDecisionUser(inviteUser);
    }

    @Action
    async changeUserPermissionAccess(payload: { userId: string, permissionAccess: string }) {
        if (!this.selectedDecisionId) return;
        let accessModification = await VIEWPOINT_API.modifyDecisionUserAccess(this.selectedDecisionId, payload.userId, payload.permissionAccess);
        let existingUser = this.selectedDecisionUsers[payload.userId];
        existingUser.permissionAccess = accessModification.permissionAccess;
        this.setSelectedDecisionUser(existingUser)
    }

    @Action
    async getDecisionLists() {
        let savedLists = await VIEWPOINT_API.getDecisionLists(this.selectedDecisionId);
        this.setDecisionsLists(_.keyBy(savedLists, "listId"));
    }

    @Action
    async joinUserToDecision(payload: { userId: string, permission }) {
        let user = await VIEWPOINT_API.joinDecisionAutocomplete(this.selectedDecisionId, payload.userId, payload.permission)
        let decisionUsersPromise = VIEWPOINT_API.getDecisionUsers(this.selectedDecisionId)
        this.setSelectedDecisionUsers(_.keyBy(await decisionUsersPromise, 'userId'));
    }

    @Action
    async cloneUsersToDecision(payload: {decisionId: string, userId: string, permission }) {
        let user = await VIEWPOINT_API.joinDecisionAutocomplete(payload.decisionId, payload.userId, payload.permission)
    }

    @Action
    async deleteChoiceFromList() {
        const choiceModule = getModule(Choices);
        const choices = choiceModule.choices;

        for (let key in this.decisionLists) {
            if (this.decisionLists[key].type === "CHOICE") {
                if (this.decisionLists[key].choices && this.decisionLists[key].choices.length > 0) {
                    let requireUpdate = false;
                    this.decisionLists[key].choices.forEach((e: any, index: number, object: any) => {
                        if (!choices[e.choiceId]) {
                            object.splice(index, 1);
                            requireUpdate = true;
                        }
                    });
                    if (requireUpdate) {
                        await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
                    }
                }
            }
        }
    }

    @Action
    async updateDecisionLists(payload: { listId: string, list: any }) {
        await VIEWPOINT_API.putList(this.selectedDecision.decisionId, payload.listId, payload.list);
    }

    @Action
    async addViewpointToActiveLists(viewpointId: string) {
        let viewpointToAdd = viewpointId
        let workspaceOptionsModule = getModule(WorkspaceOptions)
        for (let key in this.decisionLists) {
            if (this.decisionLists[key].type === "VIEWPOINT" && workspaceOptionsModule?.activeViewpointLists.includes(key)) {
                this.decisionLists[key].viewpoints.push({ viewpointId: viewpointToAdd });
                await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] })
            }
        }
    }

    @Action
    async addChoiceToActiveLists(choiceId: string) {
        let workspaceOptionsModule = getModule(WorkspaceOptions)
        for (let key in this.decisionLists) {
            if (this.decisionLists[key].type === "CHOICE" && workspaceOptionsModule?.activeChoiceLists.includes(key)) {
                this.decisionLists[key].choices.push({ choiceId: choiceId });
                await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] })
            }
        }
    }

    @Action
    async addChoicesToSelectLists(payload: {choices: Choice[], listIds: string[]}) {
        let workspaceOptionsModule = getModule(WorkspaceOptions);
        let choices = payload.choices.map(choice => { return choice.id });
        // console.log(choices, payload.listIds.findIndex);
        let requiresUpdate = false;
        for (let key of payload.listIds) {
            if (this.decisionLists[key].type === "CHOICE") {
                for(let choice of choices) {
                    if(!this.decisionLists[key].choices.map(choice => {return choice.choiceId}).includes(choice)) {
                        this.decisionLists[key].choices.push({choiceId: choice});
                        requiresUpdate = true;
                    }
                }
                if(requiresUpdate) 
                    await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
            }
        }
    }

    @Action
    async removeChoicesFromSelectLists(payload: { choices: Choice[], listIds: string[] }) {
        let workspaceOptionsModule = getModule(WorkspaceOptions);
        let choices = payload.choices.map(choice => { return choice.id });
        let requireUpdate = false;
        for (let key of payload.listIds) {
            if (this.decisionLists[key].type === "CHOICE") {
                for (let choice of choices) {
                    let index = _.findIndex(this.decisionLists[key].choices, (choiceId: any) => { return choiceId.choiceId === choice});
                    if (index >= 0) {
                        // console.log("MADE IT", index)
                        this.decisionLists[key].choices.splice(index, 1);
                        requireUpdate = true;
                    }
                }
                if(requireUpdate)
                    await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
                requireUpdate = false;
            }
        }
    }

    @Action
    async addViewpointsToSelectLists(payload: { viewpoints: Viewpoint[], listIds: string[] }) {
        let workspaceOptionsModule = getModule(WorkspaceOptions);
        let viewpoints = payload.viewpoints.map(viewpoint => { return viewpoint.viewpointId });
        let requiresUpdate = false;
        for (let key of payload.listIds) {
            if (this.decisionLists[key].type === "VIEWPOINT") {
                for (let viewpoint of viewpoints) {
                    if (!this.decisionLists[key].viewpoints.map(viewpoint => { return viewpoint.viewpointId }).includes(viewpoint)) {
                        this.decisionLists[key].viewpoints.push({ viewpointId: viewpoint });
                        requiresUpdate = true;
                    }
                }
                if (requiresUpdate)
                    await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
            }
        }
    }

    @Action
    async removeViewpointsFromSelectLists(payload: { viewpoints: Viewpoint[], listIds: string[] }) {
        let workspaceOptionsModule = getModule(WorkspaceOptions);
        let viewpoints = payload.viewpoints.map(viewpoint => { return viewpoint.viewpointId });
        let requireUpdate = false;
        for (let key of payload.listIds) {
            if (this.decisionLists[key].type === "VIEWPOINT") {
                for (let viewpoint of viewpoints) {
                    let index = _.findIndex(this.decisionLists[key].viewpoints, (viewpointId: any) => {return viewpointId.viewpointId === viewpoint });
                    if (index >= 0) {
                        this.decisionLists[key].viewpoints.splice(index, 1);
                        requireUpdate = true;
                    }
                }
                if (requireUpdate)
                    await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
                requireUpdate = false;
            }
        }
    }

    @Action
    async addFactorToActiveLists(factorId: string) {
        let workspaceOptionsModule = getModule(WorkspaceOptions)
        for (let key of workspaceOptionsModule.activeFactorLists) {
            if (this.decisionLists[key]) {
                this.decisionLists[key].factors.push({factorId: factorId});
                // console.log(this.decisionLists[key]);
                await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
            }
        }
        // console.log(factorId);
    }

    @Action
    async addFactorsToSelectLists(payload: { factors: FactorResource[], listIds: string[] }) {
        let workspaceOptionsModule = getModule(WorkspaceOptions);
        let factors = payload.factors.map(factor => { return factor.id });
        let requiresUpdate = false;
        for (let key of payload.listIds) {
            if (this.decisionLists[key].type === "FACTOR") {
                for (let factor of factors) {
                    if (!this.decisionLists[key].factors.map(factor => { return factor.factorId }).includes(factor)) {
                        this.decisionLists[key].factors.push({ factorId: factor });
                        requiresUpdate = true;
                    }
                }
                if (requiresUpdate)
                    await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
            }
        }
    }

    @Action
    async deleteViewpointFromList() {
        const viewpointModule = getModule(Viewpoints);
        const viewpoints = viewpointModule.decisionViewpointsMap;
        for (let key in this.decisionLists) {
            if (this.decisionLists[key].type === "VIEWPOINT") {
                if (this.decisionLists[key].viewpoints && this.decisionLists[key].viewpoints.length > 0) {
                    let requireUpdate = false;
                    this.decisionLists[key].viewpoints.forEach((e: any, index: number, object: any) => {
                        if (!viewpoints[e.viewpointId]) {
                            object.splice(index, 1);
                            requireUpdate = true;
                        }
                    });
                    if (requireUpdate) {
                        await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
                    }
                }
            }
        }
    }

    @Action
    async deleteFactorFromList() {
        const modellingModule = getModule(Modelling);
        const factors = modellingModule.workingModelFactorResources;
        // console.log(factors);
        for (let key in this.decisionLists) {
            if (this.decisionLists[key].type === "FACTOR") {
                if (this.decisionLists[key].factors && this.decisionLists[key].factors.length > 0) {
                    let requireUpdate = false;
                    this.decisionLists[key].factors.forEach((e: any, index: number, object: any) => {
                        if (!factors[e.factorId]) {
                            // console.log("remove" + e.factorId);
                            object.splice(index, 1);
                            requireUpdate = true;
                        }
                    });
                    if (requireUpdate) {
                        await this.updateDecisionLists({ listId: key, list: this.decisionLists[key] });
                    }
                }
            }
        }
    }

    private static async copyReplaceViewpointChoiceScores(newViewpoint: string, oldViewpoint: string, mapDict: any) {
        let oldViewpointChoiceFactorResources: ViewpointChoiceFactor[] = await VIEWPOINT_API.getViewpointChoiceFactors(oldViewpoint);
        const newViewpointChoiceFactorResources: ViewpointChoiceFactor[] = [];
        for(let viewpointChoiceFactor of oldViewpointChoiceFactorResources) {
            if (mapDict[viewpointChoiceFactor.choiceId]) {
                Vue.set(viewpointChoiceFactor, 'choiceId', mapDict[viewpointChoiceFactor.choiceId])
                Vue.set(viewpointChoiceFactor, 'viewpointId', newViewpoint);
                Vue.delete(viewpointChoiceFactor, 'id');
                newViewpointChoiceFactorResources.push(viewpointChoiceFactor);
            }

        }
        if (newViewpointChoiceFactorResources.length > 0)
            await VIEWPOINT_API.putViewpointChoiceFactors(newViewpoint, newViewpointChoiceFactorResources);
    }

    @Action
    async sendChoiceSubmissionEmail(payload: {decisionId: string, userId: string, email: string, choiceId: string, link: string, adminEmail: string, userDetails: string, userDetailsHtml: string, linkText: string }) {
        const decisionId = payload.decisionId;
        const userId = payload.userId;
        const email = payload.email;
        const adminEmail = payload.adminEmail;
        const choiceId = payload.choiceId;
        const link = payload.link;
        const linkText = payload.linkText;
        const userDetails = payload.userDetails;
        const userDetailsHtml = payload.userDetailsHtml;
        let res = await VIEWPOINT_API.sendChoiceSubmissionEmail(decisionId, email, adminEmail, userDetails, choiceId, userId, link, userDetailsHtml, linkText);
        console.log("VIEWPOINT_API.sendChoiceSubmissionEmail(decisionId, email, adminEmail, userDetails, choiceId, userId, link, userDetailsHtml, linkText)");
        console.log(res);
        return res;
    }

}